import Vue from "vue";
import { Store, Module, ActionContext } from "vuex";
import ListState from "./list-state";
import ActionTracking from "../entities/actionTracking";
import ActionCheckpoint from "../entities/actionCheckpoint";
import Ajax from "../../lib/ajax";
import ListMutations from "./list-mutations";
import Review from "../entities/review";
import Project from "../entities/project";
import Objective from "../entities/objective";
import ReviewCalculator from "../../lib/reviewCalculator";
import moment from "moment";
import Cookies from "js-cookie";

let _: any = require("lodash");

const LAST_SELECTED_GOAL_ID_KEY: string = "lastSelectedReviewId";
const LAST_SELECTED_MEMBER_ID_KEY: string = "lastSelectedMemberId";
const TARGET_USER_FILTER_KEY: string = "targetUserFilter";
const ASSESSMENT_FILTER_KEY: string = "assessmentFilter";
const DATE_FILTER_KEY: string = "dateFilter";
const cookieExpires: number = 1;

let getLastSelectedReviewIdFromCookie = function(): string {
  return Cookies.get(LAST_SELECTED_GOAL_ID_KEY);
};

let getLastSelectedMemberIdFromCookie = function(): number {
  let value: string = Cookies.get(LAST_SELECTED_MEMBER_ID_KEY);

  return value ? Number(value) : NaN;
};

let getAssessmentFilterFromCookie = function(): string {
  //return Cookies.get(ASSESSMENT_FILTER_KEY);
  return "both";
};

let getTargetUserFilterFromCookie = function(): number {
  //let value: string = Cookies.get(TARGET_USER_FILTER_KEY);
  //return value ? Number(value) : NaN;

  return 0; // 0: All Team
};

let getDateFilterFromCookie = function(): Date {
  //let value: string = Cookies.get(DATE_FILTER_KEY);
  //return value ? new Date(value) : null;

  var endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return endOfToday;
};

let getExclusionFromCookie = function(): string {
  //return Cookies.get(Exclusion_KEY);
  return "none";
};

// tslint:disable-next-line: interface-name
interface ReviewState {
  loading: boolean;
  lastSelectedReviewId: string;
  lastSelectedMemberId: number;
  targetUserFilter: number;
  assessmentFilter: string;
  dateFilter: Date;
  reviews: Review[];
  reviewsExclusion: string;
  myTrackingReviews: Review[];
  editableReviews: Review[];
  myReviews: Review[];
  myObjectives: Objective[];
  currentReview: Review;
  currentReviewLoadingCount: number;
  currentReviewHasCheckpointUpdated: boolean;
  currentReviewMemberMap: Map<number, string>;
  currentReviewMembers: {
    id: number;
    name: string;
    role: number;
    roleName: string;
  }[];
  currentReviewTrackingMembers: {
    id: number;
    name: string;
    role: number;
    roleName: string;
  }[];
  currentObjective: Objective;
  currentProject: Project;
  currentActionTrackingInformation: ActionTracking;
  currentCheckpoint: ActionCheckpoint;
  dailyQuickTrackingInformations: any;
  incompletedCheckpoints: any[];
  completedCheckpoints: any[];
}

var endOfToday = new Date();
endOfToday.setHours(23, 59, 59, 999);

export const state: ReviewState = {
  loading: false,
  lastSelectedReviewId: getLastSelectedReviewIdFromCookie() || null,
  lastSelectedMemberId: getLastSelectedMemberIdFromCookie() || null,
  targetUserFilter: getTargetUserFilterFromCookie() || 0,
  assessmentFilter: getAssessmentFilterFromCookie() || "self",
  dateFilter: getDateFilterFromCookie() || endOfToday,
  reviews: new Array<Review>(),
  reviewsExclusion: "none",
  myTrackingReviews: new Array<Review>(),
  editableReviews: new Array<Review>(),
  myReviews: new Array<Review>(),
  myObjectives: new Array<Objective>(),
  currentReview: new Review(),
  currentReviewLoadingCount: 0,
  currentReviewHasCheckpointUpdated: false,
  currentReviewMemberMap: new Map<number, string>(),
  currentReviewMembers: [],
  currentReviewTrackingMembers: [],
  currentObjective: new Objective(),
  currentProject: new Project(),
  currentActionTrackingInformation: new ActionTracking(),
  currentCheckpoint: null,
  dailyQuickTrackingInformations: null,
  incompletedCheckpoints: [],
  completedCheckpoints: []
};

const reviewModule: Module<ReviewState, any> = {
  namespaced: true,
  state,
  getters: {
    editRoleOptions: state => {
      return [
        {
          value: 1,
          label: "Review Leader Only",
          description: "The review leader only can add, edit, and delete review objectives",
          disabled: false
        },
        {
          value: 2,
          label: "Both Review Leader & Participates",
          description: "The review leader and participates can both add, edit, and delete review objectives",
          disabled: false
        }
      ];
    },
    calculationOptions: state => {
      return [
        {
          value: 1,
          label: "Last Check-in Result Only",
          description: "Display the last check-in result only when reporting review progress",
          disabled: false
        },
        {
          value: 2,
          label: "Average All Check-in Results",
          description: "Display the average of all check-in results when reporting review progress",
          disabled: false
        }
      ];
    },
    gradingOptions: state => {
      return [
        {
          value: 1,
          label: "Review Leader Only",
          description: "The review leader only grades the performance of each member in the review",
          disabled: false
        },
        {
          value: 2,
          label: "Both Review Leader & Participates",
          description: "The review leader grades performance and each participate grades own performance",
          disabled: false
        }
      ];
    },
    parentRoleTitle: state => review => {
      if (!review) {
        review = state.currentReview;
      }

      if (review) {
        return review.parentRoleName || "";
      }

      return "Parent Role";
    },
    childRoleTitle: state => review => {
      if (!review) {
        review = state.currentReview;
      }

      if (review) {
        return review.childRoleName || "";
      }

      return "Child Role";
    },
    roleTitle: (state, getters) => role => {
      if (role === 1) {
        return getters.parentRoleTitle;
      } else if (role === 2) {
        return getters.childRoleTitle;
      } else if (role === 4) {
        return "Fan";
      } else {
        return "Unknown";
      }
    },
    currentReviewGrades: state => {
      let grades = state.currentReview && state.currentReview.gradeModel ? JSON.parse(state.currentReview.gradeModel) : [];

      return grades.sort(function(a: any, b: any): number {
        return a.from - b.from;
      });
    },
    canAdd: (state, getters, rootState) => {
      let billingAccount = rootState.billing.billingAccount;
      let user = rootState.session.user;
      if (billingAccount && user) {
        let members = billingAccount.members.filter(m => m.memberId == user.id);
        if (members.length === 1) {
          let member = members[0];

          return member.grantedPermissions.includes("Bio.AddBio");
        }
      }

      return false;
    },
    canEdit: state => review => {
      return review && review.grantedPermissions && review.grantedPermissions.includes("Bio.EditBio");
    },
    canEditCurrentReview: state => {
      return state.currentReview && state.currentReview.grantedPermissions && state.currentReview.grantedPermissions.includes("Bio.EditBio");
    },
    canInviteMemberInCurrentReview: (state, getters, rootState) => {
      let billingAccount = rootState.billing.billingAccount;
      let user = rootState.session.user;

      if (state.currentReview && state.currentReview.grantedPermissions && billingAccount && user) {
        let members = billingAccount.members.filter(m => m.memberId == user.id);
        if (members.length === 1) {
          let member = members[0];

          let permissions = state.currentReview.grantedPermissions.concat(member.grantedPermissions);
          return permissions.includes("Bio.AssignAuthorRole") || permissions.includes("Bio.AssignParentRole") || permissions.includes("Bio.AssignChildRole") || permissions.includes("Bio.AssignFan");
        }
      }

      return false;
    },
    getRoleDetails: (state, getters, rootState) => (role, review) => {
      if (!review) {
        review = state.currentReview;
      }
      if (role === 1) {
        return {
          value: 1,
          name: "parent",
          title: `${getters.parentRoleTitle(review)} (review leader)`,
          description: "Review leader is the person leading the review process."
        };
      } else if (role === 2) {
        return {
          value: 2,
          name: "child",
          title: `${getters.childRoleTitle(review)} (review participate)`,
          description: "Review participate is the person participating in the review process."
        };
      } else if (role === 4) {
        return {
          value: 4,
          name: "fan",
          title: "Fan only",
          description: 'Fan only can "see-only" all review information. Not able to add, edit, and delete review information. Not accountable to chart review check-in results.'
        };
      } else if (role === 5) {
        return {
          value: 5,
          name: "author",
          title: "Author only",
          description: "Author only can add, edit, and delete review information. Not accountable to chart review check-in results."
        };
      } else {
        return null;
      }
    },
    currentReviewInviteRoles: (state, getters, rootState) => (memberId, review) => {
      let billingAccount = rootState.billing.billingAccount;
      if (!review) {
        review = state.currentReview;
      }

      let results = [];
      if (review && review.grantedPermissions && billingAccount) {
        let members = billingAccount.members.filter(m => m.memberId == memberId);
        if (members.length === 1) {
          let member = members[0];

          results.push(
            Object.assign(
              {
                disabled: !(review.grantedPermissions.includes("Bio.AssignParentRole") && member.grantedPermissions.includes("Bio.BeParent"))
              },
              getters.getRoleDetails(1, review)
            )
          );

          results.push(
            Object.assign(
              {
                disabled: !(review.grantedPermissions.includes("Bio.AssignChildRole") && member.grantedPermissions.includes("Bio.BeChild"))
              },
              getters.getRoleDetails(2, review)
            )
          );

          results.push(
            Object.assign(
              {
                disabled: !(review.grantedPermissions.includes("Bio.AssignAuthorRole") && member.grantedPermissions.includes("Bio.BeAuthor"))
              },
              getters.getRoleDetails(5, review)
            )
          );

          results.push(
            Object.assign(
              {
                disabled: !(review.grantedPermissions.includes("Bio.AssignFan") && member.grantedPermissions.includes("Bio.BeFan"))
              },
              getters.getRoleDetails(4, review)
            )
          );
        }
      }
      return results;
    }
  },
  actions: {
    async getMyReviews(context: any, payload: any) {
      context.state.loading = true;
      let billingAccountId = window.abp && window.abp.custom.currentBillingAccountId ? window.abp.custom.currentBillingAccountId : 0;
      await Ajax.get(`/api/services/app/Review/getMyReviews?billingAccountId=${billingAccountId}&exclusion=`).then(response => {
        let reviews = response.data.result.myReviews;
        reviews.forEach(review => {
          review.hashTagList = review.hashTags ? JSON.parse(review.hashTags) : [];
        });
        context.state.reviews = reviews;
        context.state.myReviews = reviews;
        context.state.myObjectives = response.data.result.myObjectives;
      });
      context.state.loading = false;
    },
    async getEditableReviews(context: any, payload: any) {
      await Ajax.get("/api/services/app/Review/GetEditableReviews").then(response => {
        context.state.editableReviews = response.data.result;
      });
    },
    async getMyTrackingReviews(context: any, payload: any) {
      context.state.loading = true;
      let billingAccountId = window.abp && window.abp.custom.currentBillingAccountId ? window.abp.custom.currentBillingAccountId : 0;
      await Ajax.get(`/api/services/app/Review/GetMyTrackingReviews?billingAccountId=${billingAccountId}`)
        .then(response => {
          let reviews = response.data.result;
          reviews.forEach(review => {
            review.hashTagList = review.hashTags ? JSON.parse(review.hashTags) : [];
          });
          context.state.myTrackingReviews = reviews;
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    create(context: ActionContext<ReviewState, any>, payload: any) {
      let billingAccountId = window.abp && window.abp.custom.currentBillingAccountId ? window.abp.custom.currentBillingAccountId : 0;
      payload.data.billingAccountId = billingAccountId;

      return Ajax.post("/api/services/app/Review/Create", payload.data).then(response => {
        context.dispatch("getMyReviews", {});
      });
    },
    update(context: ActionContext<ReviewState, any>, payload: any) {
      return Ajax.put("/api/services/app/Review/Update", payload.data).then(response => {
        if (context.state.currentReview && context.state.currentReview.id === payload.data.id) {
          let review = response.data.result as Review;
          loadReview(context, review);
        }

        context.dispatch("getMyReviews", {});
      });
    },
    copy(context: ActionContext<ReviewState, any>, payload: any) {
      let billingAccountId = window.abp && window.abp.custom.currentBillingAccountId ? window.abp.custom.currentBillingAccountId : 0;
      payload.data.billingAccountId = billingAccountId;
      Ajax.post("/api/services/app/Review/Copy", payload.data).then(response => {
        context.dispatch("getMyReviews", {});
      });
    },
    delete(context: ActionContext<ReviewState, any>, payload: any) {
      Ajax.delete("/api/services/app/Review/Delete?Id=" + payload.data.id).then(response => {
        context.dispatch("getMyReviews", {});
      });
    },
    async undelete(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      await Ajax.post("/api/services/app/Review/Undelete?Id=" + payload.data.id);
      context.state.loading = false;
    },
    async unarchive(context: ActionContext<ReviewState, any>, payload: any) {
      Ajax.post("/api/services/app/Review/Unarchive?Id=" + payload.data.id).then(response => {
        context.dispatch("getMyReviews", {});
      });
    },
    async get(context: any, payload: any) {
      context.state.loading = true;
      await Ajax.get("/api/services/app/Review/Get?Id=" + payload.id).then(response => {
        let review = response.data.result as Review;
        loadReview(context, review);
      });
      context.state.loading = false;

      return context.state.currentReview;
    },
    async acceptInvitation(context: ActionContext<ReviewState, any>, payload: any) {
      let result = null;

      await Ajax.post("/api/services/app/Review/AcceptInvitation", payload).then(response => {
        result = response;
      });

      return result;
    },
    async inviteMember(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.post("/api/services/app/Review/InviteMember", payload.data)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async updateMember(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.put("/api/services/app/Review/UpdateMember", payload.data)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async removeMember(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      let queryString = Object.keys(payload.data)
        .map(key => {
          return encodeURIComponent(key) + "=" + encodeURIComponent(payload.data[key]);
        })
        .join("&");
      return Ajax.delete(`/api/services/app/Review/RemoveMember?${queryString}`)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async upsertTeam(context: ActionContext<ReviewState, any>, payload: any) {
      return Ajax.post("/api/services/app/Review/UpsertTeam", payload.data);
    },
    async removeTeam(context: ActionContext<ReviewState, any>, payload: any) {
      let queryString = Object.keys(payload.data)
        .map(key => {
          return encodeURIComponent(key) + "=" + encodeURIComponent(payload.data[key]);
        })
        .join("&");
      return Ajax.delete(`/api/services/app/Review/RemoveTeam?${queryString}`);
    },
    async createObjective(context: ActionContext<ReviewState, any>, payload: any) {
      await Ajax.post("/api/services/app/Objective/Create", payload.data);
    },
    async getObjective(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      await Ajax.get("/api/services/app/Objective/Get?Id=" + payload.id).then(response => {
        context.state.currentObjective = response.data.result as Objective;
      });
      context.state.loading = false;

      return context.state.currentObjective;
    },
    async upsertObjective(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.post("/api/services/app/Review/UpsertObjective", payload.data)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async updateCheckpoint(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.put("/api/services/app/Review/UpdateCheckpoint", payload.data)
        .then(response => {
          //let review = response.data.result as Review;
          //loadReview(context, review);

          context.state.currentReviewHasCheckpointUpdated = true;
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async copyObjective(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.post("/api/services/app/Review/CopyObjective", payload.data)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async deleteObjective(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      await Ajax.delete(`/api/services/app/Review/DeleteObjective?ReviewId=${payload.data.reviewId}&Id=${payload.data.id}`)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    async undeleteObjective(context: ActionContext<ReviewState, any>, payload: any) {
      context.state.loading = true;
      return Ajax.post(`/api/services/app/Review/UndeleteObjective?ReviewId=${payload.data.reviewId}&Id=${payload.data.id}`)
        .then(response => {
          let review = response.data.result as Review;
          loadReview(context, review);
        })
        .finally(() => {
          context.state.loading = false;
        });
    },
    setLastSelectedReviewId(context: ActionContext<ReviewState, any>, newValue: string) {
      Cookies.set(LAST_SELECTED_GOAL_ID_KEY, newValue, { expires: cookieExpires });
      context.state.lastSelectedReviewId = newValue;
    },
    setLastSelectedMemberId(context: ActionContext<ReviewState, any>, newValue: string) {
      Cookies.set(LAST_SELECTED_MEMBER_ID_KEY, newValue, { expires: cookieExpires });
      context.state.lastSelectedMemberId = Number(newValue);
    },
    setTargetUserFilter(context: ActionContext<ReviewState, any>, newValue: string) {
      Cookies.set(TARGET_USER_FILTER_KEY, newValue, { expires: cookieExpires });
      context.state.targetUserFilter = Number(newValue);
      context.dispatch("recalculateTrackingResults");
    },
    setAssessmentFilter(context: ActionContext<ReviewState, any>, newValue: string) {
      Cookies.set(ASSESSMENT_FILTER_KEY, newValue, { expires: cookieExpires });
      context.state.assessmentFilter = newValue;
      context.dispatch("recalculateTrackingResults");
    },
    setDateFilter(context: ActionContext<ReviewState, any>, newValue: Date) {
      Cookies.set(DATE_FILTER_KEY, newValue.toString(), { expires: cookieExpires });
      context.state.dateFilter = newValue;
      context.dispatch("recalculateTrackingResults");
    },
    setReviewExclusionFilter(context: ActionContext<ReviewState, any>, newValue: string) {
      context.state.reviewsExclusion = newValue;
    },
    recalculateTrackingResults(context: ActionContext<ReviewState, any>) {
      let review = context.state.currentReview;
      if (review.gradingOption === 1) {
        Cookies.set(ASSESSMENT_FILTER_KEY, "both", { expires: cookieExpires });
        context.state.assessmentFilter = "both";
      }

      let exclusion = context.state.currentReview.calculationOption === 1 ? "lastCheckin" : "none";
      ReviewCalculator.calculateReview(context.state.currentReview, context.state.targetUserFilter, context.state.assessmentFilter, context.state.dateFilter, exclusion);
    },
    async changeMyRoleList(context: ActionContext<ReviewState, any>, payload: any) {
      let result = null;
      await Ajax.post("/api/services/app/Configuration/ChangeMyRoleList", payload).then(response => {
        result = response;
      });

      return result;
    },
    async changeMyGradeList(context: ActionContext<ReviewState, any>, payload: any) {
      let result = null;
      await Ajax.post("/api/services/app/Configuration/ChangeMyGradeList", payload).then(response => {
        result = response;
      });

      return result;
    },
    async changeMyTargetScaleList(context: ActionContext<ReviewState, any>, payload: any) {
      let result = null;
      await Ajax.post("/api/services/app/Configuration/ChangeMyTargetScaleList", payload).then(response => {
        result = response;
      });

      return result;
    },
    async changeMyUnitList(context: ActionContext<ReviewState, any>, payload: any) {
      let result = null;
      await Ajax.post("/api/services/app/Configuration/ChangeMyUnitList", payload).then(response => {
        result = response;
      });

      return result;
    }
  }
};

function loadReview(context: ActionContext<ReviewState, any>, review: Review) {
  review.objectives = review.content ? JSON.parse(review.content).objectives : [];

  context.state.currentReview = review;
  context.state.currentReviewLoadingCount += 1;
  context.state.currentReviewHasCheckpointUpdated = false;

  let memberMap = new Map(review.members.map((member): [number, string] => [member.memberId, member.memberName]));
  let checkpoints = [];
  if (review && review.objectives) {
    review.objectives.forEach(objective => {
      if (objective && objective.checkpoints) {
        checkpoints = checkpoints.concat(
          objective.checkpoints.map(checkpoint => {
            checkpoint.targetUserName = memberMap.get(checkpoint.targetUserId);
            checkpoint.trackerName = memberMap.get(checkpoint.trackerId);
            return {
              dateString: moment(checkpoint.trackingDate).format("YYYY-MM-DD"),
              path: `${review.name} ${objective.name}`,
              review: review.name,
              objective: objective.name,
              priority: objective.priority,
              objectivePriority: objective.priority,
              data: {
                role: "",
                reviewName: review.name,
                objectiveName: objective.name,
                reviewId: review.id,
                objectiveId: objective.id,
                id: objective.id,
                priority: objective.priority,
                targetType: objective.targetType,
                measurementUnit: objective.measurementUnit,
                inputOptions: objective.inputOptions,
                trackingTarget: objective.trackingTarget,
                trackingQuestion: objective.trackingQuestion,
                attachments: objective.attachments,
                checkpoint: checkpoint
              }
            };
          })
        );
      }
    });
  }

  context.state.incompletedCheckpoints = checkpoints.filter(cp => cp.data.checkpoint.value === null || cp.data.checkpoint.value === "");
  context.state.completedCheckpoints = checkpoints.filter(cp => cp.data.checkpoint.value !== null && cp.data.checkpoint.value !== "");

  if (review.gradingOption === 1) {
    Cookies.set(ASSESSMENT_FILTER_KEY, "both", { expires: cookieExpires });
    context.state.assessmentFilter = "both";
  }

  context.state.currentReviewMembers = review.members
    .filter(member => member.role !== 4)
    .map(member => {
      let roleName = member.role === 1 ? review.parentRoleName : member.role === 2 ? review.childRoleName : "";
      return {
        id: member.memberId,
        name: `${member.memberName} - ${roleName}`,
        role: member.role,
        roleName: roleName
      };
    });

  context.state.currentReviewTrackingMembers = context.state.currentReviewMembers.filter(member => member.role === 2);

  let trackerFilterChanged = false;
  let lastFilterTracker = context.state.currentReviewTrackingMembers.filter(member => member.id === context.state.targetUserFilter);
  if (lastFilterTracker.length === 0) {
    let newFilterValue = context.state.currentReviewTrackingMembers.length === 1 ? context.state.currentReviewTrackingMembers[0].id : "0";
    if (newFilterValue !== context.state.targetUserFilter) {
      context.dispatch("setTargetUserFilter", newFilterValue);
      trackerFilterChanged = true;
    }
  }

  if (!trackerFilterChanged) {
    let exclusion = review.calculationOption === 1 ? "lastCheckin" : "none";
    ReviewCalculator.calculateReview(review, context.state.targetUserFilter, context.state.assessmentFilter, context.state.dateFilter, exclusion);
  }
}

export default reviewModule;
